import { makeStyles, Spinner } from '@fluentui/react-components';
import { ottoTokens } from '@/theme/ottoTokens';
import {
  CheckmarkCircleFilled,
  DismissCircleFilled,
  WarningFilled,
  CircleFilled,
} from '@fluentui/react-icons';
import type { RunStatus } from '@/types/monitoring';

type RunStatusIconProps = {
  status: RunStatus;
  size?: 'small' | 'medium' | 'large';
};

const useStyles = makeStyles({
  succeeded: {
    color: ottoTokens.statusSucceeded,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
  failed: {
    color: ottoTokens.statusFailed,
    '@media (forced-colors: active)': {
      color: 'LinkText',
    },
  },
  cancelled: {
    color: ottoTokens.statusCancelled,
    '@media (forced-colors: active)': {
      color: 'GrayText',
    },
  },
  running: {
    color: ottoTokens.statusRunning,
    '@media (forced-colors: active)': {
      color: 'Highlight',
    },
  },
  pending: {
    color: ottoTokens.statusPending,
    '@media (forced-colors: active)': {
      color: 'GrayText',
    },
  },
});

const getIconSize = (size: 'small' | 'medium' | 'large'): number => {
  switch (size) {
    case 'small':
      return 16;
    case 'large':
      return 24;
    case 'medium':
    default:
      return 20;
  }
};

export const RunStatusIcon = ({ status, size = 'medium' }: RunStatusIconProps) => {
  const styles = useStyles();
  const iconSize = getIconSize(size);

  if (status === 'running') {
    return (
      <Spinner
        size="extra-tiny"
        className={styles.running}
        aria-label="Running"
      />
    );
  }

  switch (status) {
    case 'succeeded':
      return (
        <CheckmarkCircleFilled
          className={styles.succeeded}
          fontSize={iconSize}
          aria-label="Succeeded"
        />
      );
    case 'failed':
      return (
        <DismissCircleFilled
          className={styles.failed}
          fontSize={iconSize}
          aria-label="Failed"
        />
      );
    case 'cancelled':
      return (
        <WarningFilled
          className={styles.cancelled}
          fontSize={iconSize}
          aria-label="Cancelled"
        />
      );
    default:
      return (
        <CircleFilled
          className={styles.pending}
          fontSize={iconSize}
          aria-label={status}
        />
      );
  }
};
